import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Plus, ClipboardList } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { useUser } from '@/shared/hooks/useAuth';
import { useTasks, useTaskMutations, useProjectOptions } from '../hooks/useTasks';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { TasksFilterBar } from '../components/TasksFilterBar';
import { TasksKanban } from '../components/TasksKanban';
import { TaskFormModal } from '../components/TaskFormModal';
import { TaskDrawer } from '../components/TaskDrawer';
import { SavedViewsMenu } from '@/features/views/components/SavedViewsMenu';
import type { ViewFilters } from '@/features/views/types';
import { BulkActionBar } from '@/features/bulk/components/BulkActionBar';
import { useRowSelection } from '@/features/bulk/hooks/useRowSelection';
import type { Task, TaskStatus, TasksFilters, CreateTaskPayload, UpdateTaskPayload } from '../types';

export function TasksPage() {
  const user = useUser();
  const isAdmin = ['superadmin', 'admin', 'manager', 'team_lead', 'bde'].includes(user?.role ?? '');

  // ─── Filters ──────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [projectId, setProjectId] = useState('');
  const [assignedTo, setAssignedTo] = useState('');

  const filters: TasksFilters = {
    ...(projectId ? { projectId } : {}),
    ...(assignedTo ? { assignedTo } : {}),
  };

  // ─── Data ─────────────────────────────────────────────────────────────────
  const { data: tasks = [], isLoading, isError } = useTasks(filters);
  const { data: projects = [] } = useProjectOptions();
  const { data: users = [] } = useUserOptions();
  const validUsers = useMemo(() => {
    return ['superadmin', 'admin', 'bde'].includes(user?.role ?? '')
      ? users
      : users.filter(u => u.department === user?.department);
  }, [users, user?.role, user?.department]);

  const { createTask, updateTask, deleteTask } = useTaskMutations();

  // Saved views (Req 7.1–7.5): only the allowlisted task filters this list
  // exposes (projectId, assignedTo). Client-only text search is not saved.
  const savedViewFilters = useMemo<ViewFilters>(() => {
    const f: ViewFilters = {};
    if (projectId) f.projectId = projectId;
    if (assignedTo) f.assignedTo = assignedTo;
    return f;
  }, [projectId, assignedTo]);

  const handleApplyView = (query: ViewFilters) => {
    setProjectId(typeof query.projectId === 'string' ? query.projectId : '');
    setAssignedTo(typeof query.assignedTo === 'string' ? query.assignedTo : '');
  };

  // Multi-select for bulk status updates (Req 8.1). Scope selection to the tasks
  // actually visible on the board (same client-side search filter the board uses)
  // so select-all and the submitted payload only ever cover on-screen tasks.
  const visibleTaskIds = useMemo(() => {
    const q = search.trim().toLowerCase();
    const shown = q
      ? tasks.filter(
          (t) =>
            t.title.toLowerCase().includes(q) ||
            t.assignee_name?.toLowerCase().includes(q) ||
            t.project_name?.toLowerCase().includes(q),
        )
      : tasks;
    return shown.map((t) => t.id);
  }, [tasks, search]);

  const selection = useRowSelection(visibleTaskIds);

  // ─── UI State ─────────────────────────────────────────────────────────────
  const [formOpen, setFormOpen] = useState(false);
  const [editTask, setEditTask] = useState<Task | null>(null);
  const [drawerTask, setDrawerTask] = useState<Task | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Task | null>(null);

  // A task_assigned notification links here with ?taskId= — consumed once
  // the task is found in the loaded list, then stripped from the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const taskId = searchParams.get('taskId');
    if (!taskId || tasks.length === 0) return;
    const found = tasks.find((t) => t.id === taskId);
    if (found) {
      setDrawerTask(found);
      const next = new URLSearchParams(searchParams);
      next.delete('taskId');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, tasks, setSearchParams]);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleCreate = () => {
    setEditTask(null);
    setFormOpen(true);
  };

  const handleEdit = useCallback((task: Task) => {
    setEditTask(task);
    setFormOpen(true);
  }, []);

  const handleOpenDrawer = useCallback((task: Task) => {
    setDrawerTask(task);
  }, []);

  const handleFormSubmit = async (payload: CreateTaskPayload | UpdateTaskPayload) => {
    if (editTask) {
      // Update
      updateTask.mutate(
        { id: editTask.id, payload: payload as UpdateTaskPayload },
        {
          onSuccess: () => {
            toast.success('Task updated.');
            setFormOpen(false);
            setEditTask(null);
          },
          onError: () => toast.error('Failed to update task.'),
        },
      );
    } else {
      // Create
      createTask.mutate(payload as CreateTaskPayload, {
        onSuccess: () => {
          toast.success('Task created.');
          setFormOpen(false);
        },
        onError: () => toast.error('Failed to create task.'),
      });
    }
  };

  const handleDrawerSave = (id: string, payload: UpdateTaskPayload) => {
    updateTask.mutate(
      { id, payload },
      {
        onSuccess: () => {
          toast.success('Task updated.');
          setDrawerTask(null);
        },
        onError: () => toast.error('Failed to update task.'),
      },
    );
  };

  const handleQuickStatus = (id: string, status: TaskStatus) => {
    updateTask.mutate(
      { id, payload: { status } },
      {
        onSuccess: () => toast.success(`Task → ${status.replace('_', ' ')}`, { duration: 2000 }),
        onError: () => toast.error('Failed to update status.'),
      },
    );
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    deleteTask.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success('Task deleted.');
        setDeleteTarget(null);
      },
      onError: () => toast.error('Failed to delete task.'),
    });
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="flex h-full flex-col gap-4 pb-16">
      {/* Page Header */}
      <PageHeader
        icon={ClipboardList}
        eyebrow="Workspace"
        title="Task Management"
        description={<>{tasks.length} task{tasks.length !== 1 ? 's' : ''} total</>}
        actions={
          <div className="flex items-center gap-2">
                  <SavedViewsMenu entityType="task" currentFilters={savedViewFilters} onApply={handleApplyView} />
                  <Button onClick={handleCreate} size="md">
                    <Plus className="h-4 w-4" />
                    New Task
                  </Button>
                </div>
        }
      />

      {/* Filter Bar */}
      <TasksFilterBar
        search={search}
        projectId={projectId}
        assignedTo={assignedTo}
        isAdmin={isAdmin}
        projects={projects}
        users={validUsers}
        onSearchChange={setSearch}
        onProjectChange={setProjectId}
        onAssignedToChange={setAssignedTo}
      />

      {/* Board */}
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : isError ? (
        <EmptyState
          title="Failed to load tasks"
          description="Check your connection or try refreshing."
        />
      ) : tasks.length === 0 && !search ? (
        <EmptyState
          title="No tasks yet"
          description="Create your first task to get started."
          action={<Button onClick={handleCreate}>New Task</Button>}
        />
      ) : (
        <>
          {selection.count > 0 && (
            <BulkActionBar
              entity="task"
              selectedIds={selection.selectedIds}
              onClearSelection={selection.clear}
            />
          )}
          <TasksKanban
            tasks={tasks}
            search={search}
            onEdit={handleEdit}
            onDelete={setDeleteTarget}
            onOpenDrawer={handleOpenDrawer}
            onStatusChange={handleQuickStatus}
            selection={{ isSelected: selection.isSelected, onToggle: selection.toggle }}
          />
        </>
      )}

      {/* Create / Edit Modal */}
      <TaskFormModal
        open={formOpen}
        task={editTask}
        projects={projects}
        users={validUsers}
        isAdmin={isAdmin}
        loading={createTask.isPending || updateTask.isPending}
        onClose={() => {
          setFormOpen(false);
          setEditTask(null);
        }}
        onSubmit={handleFormSubmit}
      />

      {/* Detail Drawer */}
      <TaskDrawer
        task={drawerTask}
        projects={projects}
        users={validUsers}
        isAdmin={isAdmin}
        saving={updateTask.isPending}
        onClose={() => setDrawerTask(null)}
        onSave={handleDrawerSave}
      />

      {/* Delete Confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Task"
        message={`Are you sure you want to delete "${deleteTarget?.title}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        loading={deleteTask.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
